import React, { useState, MouseEvent, useRef, useEffect, useMemo } from 'react';
import Autocomplete, { } from '@mui/material/Autocomplete';
import TextField from '@mui/material/TextField';
import Box from '@mui/material/Box';
import { TagsProps, AutocompleteTagType, AutoCompleteChangeReasons } from './Tags.props';
import { ColorSelector } from '../../molecules/ColorSelector';
import { isString } from '../../../utils/common';
import { defaultColors } from '../../../configs/colors';
import { ChipStyled } from './Tags.styles';
import { SizeProps, Tag } from '../../../@types';
import { useSearchTags } from '../../../queries/live-search-query';

export const Tags: React.FC<TagsProps> = ({
  allTags,
  selectedTags,
  setSeletedTags,
  newTags,
  setNewTags,
  inputRef,
  label,
  placeHolder,
  disabled,
  enableLiveSearch = false,
  liveSearchEntityTypeId
}) => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [selectedTag, setSelectedTag] = useState<AutocompleteTagType | string>();
  const [searchOptions, setSearchOptions] = useState<AutocompleteTagType[]>([]);
  const open = Boolean(anchorEl);
  const timerRef = useRef<NodeJS.Timeout>(undefined);
  
  const searchTags = useSearchTags();

  const handleClick = (event: MouseEvent<any>, option: AutocompleteTagType | string) => {
    if (isString(option) || !option.isSystem) {
      setAnchorEl(event.currentTarget);
      setSelectedTag(option);
    }
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const onChangeSelectedColor = (color: string) => {
    if (isString(selectedTag)) {
      const newTag = newTags.find(item => item.text === selectedTag);
      if (newTag) {
        newTag.colour = color;
      }
    }
    else {
      const tag = selectedTags.find((item) => !isString(item) && item.id == selectedTag?.id);
      if (tag && !isString(tag)) {
        tag.color = color;
      }
      setSeletedTags(selectedTags);
    }
  };

  const setChipColor = (option: AutocompleteTagType | string) => {
    if (isString(option)) {
      const newTag = newTags.find(tag => tag.text === option);
      return newTag?.colour;
    }

    return option.color;
  };

  const baseOptions = useMemo(
    () => allTags.map(option => ({ id: option.id, label: option.text, isSystem: option.isSystem, color: option.colour })),
    [allTags]
  );

  const options = enableLiveSearch && searchOptions.length > 0 ? searchOptions : baseOptions;

  useEffect(() => {
    return () => clearTimeout(timerRef.current);
  }, []);

  const handleTagSearch = async (searchText: string) => {
    if (!enableLiveSearch || !liveSearchEntityTypeId) {
      return;
    }

    clearTimeout(timerRef.current);

    // Character limit for search is set to 3. Can be changed based on requirements.
    if (searchText.length < 3) {
      setSearchOptions([]);
      return;
    }

    timerRef.current = setTimeout(async () => {
      const response = await searchTags.mutateAsync({
        searchText,
        isActive: true,
        entityTypeId: liveSearchEntityTypeId
      });

      const mappedSearchOptions: AutocompleteTagType[] = (response || []).map((tag: any) => ({
        id: tag.id,
        label: tag.text,
        isSystem: tag.isSystem,
        color: tag.colour || tag.color || defaultColors[0]
      }));

      setSearchOptions(mappedSearchOptions);
    }, 400);
  };

  return (
    <>
      <ColorSelector open={open} handleClose={handleClose} anchorEl={anchorEl} selectedColor={selectedTag && setChipColor(selectedTag)} onChangeSelectedColor={onChangeSelectedColor} />
      <Autocomplete
        disabled = {disabled}
        multiple
        id="tags-filled"
        size={SizeProps.Small}
        options={options}
        freeSolo
        renderOption={(props, option) => {
          const optionLabel = isString(option) ? option : option.label;
          const optionColor = isString(option) ? defaultColors[0] : option.color;
          const optionIsSystem = !isString(option) && option.isSystem;

          return (
            <li {...props}>
              <Box>
                <ChipStyled
                  size={SizeProps.Small}
                  isSystem={optionIsSystem}
                  bgColor={optionColor}
                  variant="outlined"
                  label={optionLabel}
                />
              </Box>
            </li>
          );
        }}
        renderTags={(value: readonly (AutocompleteTagType | string)[], getTagProps) =>
          value.map((option: AutocompleteTagType | string, index: number) => (
            // key is assigning through getTagProps with other props 
            // eslint-disable-next-line react/jsx-key
            (<ChipStyled clickable bgColor={setChipColor(option)} size={SizeProps.Small} isSystem={!isString(option) && option.isSystem} variant="outlined" label={!isString(option) ? option.label : option} {...getTagProps({ index })} onClick={(event) => handleClick(event, option)} />))
          )
        }
        onChange={(event, value, reason) => {
          if (reason === AutoCompleteChangeReasons.Clear) {
            setNewTags([]);
          }

          if (reason === AutoCompleteChangeReasons.RemoveOption || reason === AutoCompleteChangeReasons.CreateOption) {
            const newlyAssignedTags: Tag[] = [];
            value.map(item => {
              if (isString(item)) {
                const newTag = newTags.find(tag => tag.text === item);

                if (newTag) {
                  newlyAssignedTags.push(newTag);
                }
                else {
                  newlyAssignedTags.push({ id: 0, text: item, colour: defaultColors[0], isSystem: false });
                }
              }
            });
            setNewTags(newlyAssignedTags);
          }

          setSeletedTags(value);
        }}
        isOptionEqualToValue={(option, value) => {
          if (!isString(option) && typeof value !== 'string')
            return option.id === value.id;
          return option === value;
        }}
        value={selectedTags}
        renderInput={(params) => (
          <TextField
            {...params}
            label={label || 'Tags'}
            inputRef={inputRef}
            placeholder={placeHolder || 'Add more tags'}
          />
        )}
        onInputChange={(_event, value) => handleTagSearch(value)}
      />
    </>
  );
};